// Country-aware addressing for the composite 'address' field: the country
// list, and per-country conventions (which sub-fields exist and what they are
// called). Mirrored conceptually by server/src/lib/composite.ts for display —
// keep the value keys (line1/line2/city/state/postal/country) in lockstep.
import { CA_PROVINCES, US_STATES } from './states';

export const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

/**
 * The sub-answers an 'address' field stores. Blank parts are null, and
 * `fullAddress` is derived (recomputed on every edit) so integrations can map
 * either the one-line answer or any individual part.
 */
export interface AddressValue {
  fullAddress?: string | null;
  /** shipping addresses only */
  company?: string | null;
  /** shipping addresses only */
  attention?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  /** shipping addresses only — a contact number, kept out of fullAddress */
  phone?: string | null;
}

/** The sub-answers a 'name' field stores. `fullName` is derived like fullAddress. */
export interface NameValue { fullName?: string | null; firstName?: string | null; lastName?: string | null }

/**
 * One sub-answer, AS TYPED. Whitespace-only is blank, but anything else is
 * kept verbatim — these functions run on every keystroke, and trimming here
 * would eat the space the moment someone typed it, making "Main Street"
 * impossible to enter. Padding is dropped where it actually matters: out of
 * the derived combined value below, and out of the submission (trimComposite).
 */
const part = (v: unknown): string | null => {
  const s = String(v ?? '');
  return s.trim() ? s : null;
};

/** A sub-answer as it reads inside the combined value, where padding is noise. */
const joined = (v: string | null): string => (v ? v.trim() : '');

/** Materialize every key (null for blanks) and recompute the combined value. */
export function normalizeNameValue(raw: Record<string, unknown>): NameValue {
  const firstName = part(raw.firstName ?? raw.first);
  const lastName = part(raw.lastName ?? raw.last);
  return { fullName: [firstName, lastName].map(joined).filter(Boolean).join(' ') || null, firstName, lastName };
}

/** Materialize every key (null for blanks) and recompute the combined value.
 *  `shipping` addresses additionally carry company/attention/phone; the phone
 *  stays out of fullAddress (it is a contact detail, not an address line). */
export function normalizeAddressValue(raw: Record<string, unknown>, opts: { shipping?: boolean } = {}): AddressValue {
  const addressLine1 = part(raw.addressLine1 ?? raw.line1);
  const addressLine2 = part(raw.addressLine2 ?? raw.line2);
  const city = part(raw.city);
  const state = part(raw.state);
  const postalCode = part(raw.postalCode ?? raw.postal);
  const country = part(raw.country);
  const company = opts.shipping ? part(raw.company) : null;
  const attention = opts.shipping ? part(raw.attention) : null;
  const parts = [company, attention, addressLine1, addressLine2, city, state, postalCode]
    .map(joined).filter(Boolean);
  return {
    // A country alone (usually just the pre-selected default) is not an address.
    fullAddress: parts.length ? [...parts, joined(country)].filter(Boolean).join(', ') : null,
    ...(opts.shipping ? { company, attention } : {}),
    addressLine1, addressLine2, city, state, postalCode, country,
    ...(opts.shipping ? { phone: part(raw.phone) } : {}),
  };
}

/** How one country labels (and which of) the address sub-fields. */
export interface AddressFormat {
  cityLabel: string;
  /** null = the country has no state/province line */
  stateLabel: string | null;
  /** a fixed list renders a dropdown; null renders free text */
  stateOptions: string[] | null;
  /** required alongside the rest when the whole field is required */
  stateRequired: boolean;
  /** null = the country has no postal-code line */
  postalLabel: string | null;
}

const fmt = (p: Partial<AddressFormat>): AddressFormat => ({
  cityLabel: 'City',
  stateLabel: 'State / Province / Region',
  stateOptions: null,
  stateRequired: false,
  postalLabel: 'Postal Code',
  ...p,
});

const DEFAULT_FORMAT = fmt({});

/** Per-country conventions. Anything not listed uses the generic default. */
const FORMATS: Record<string, AddressFormat> = {
  'United States': fmt({ stateLabel: 'State', stateOptions: US_STATES, stateRequired: true, postalLabel: 'ZIP Code' }),
  Canada: fmt({ stateLabel: 'Province', stateOptions: CA_PROVINCES, stateRequired: true, postalLabel: 'Postal Code' }),
  'United Kingdom': fmt({ cityLabel: 'Town / City', stateLabel: 'County', postalLabel: 'Postcode' }),
  Australia: fmt({ cityLabel: 'Suburb / Town', stateLabel: 'State / Territory', stateOptions: AU_STATES, stateRequired: true, postalLabel: 'Postcode' }),
  'New Zealand': fmt({ cityLabel: 'Town / City', stateLabel: 'Region', postalLabel: 'Postcode' }),
  Ireland: fmt({ cityLabel: 'Town / City', stateLabel: 'County', postalLabel: 'Eircode' }),
  India: fmt({ stateLabel: 'State', stateRequired: true, postalLabel: 'PIN Code' }),
  Japan: fmt({ stateLabel: 'Prefecture', stateRequired: true }),
  Brazil: fmt({ stateLabel: 'State', stateRequired: true, postalLabel: 'CEP' }),
  Mexico: fmt({ stateLabel: 'State', stateRequired: true }),
  China: fmt({ stateLabel: 'Province' }),
  'South Korea': fmt({ stateLabel: 'Province' }),
  Philippines: fmt({ stateLabel: 'Province', postalLabel: 'ZIP Code' }),
  'United Arab Emirates': fmt({ stateLabel: 'Emirate', postalLabel: null }),
  'Hong Kong': fmt({ stateLabel: 'District', postalLabel: null }),
  Singapore: fmt({ stateLabel: null }),
  Germany: fmt({ stateLabel: null }),
  France: fmt({ stateLabel: null }),
  Italy: fmt({ stateLabel: 'Province' }),
  Spain: fmt({ stateLabel: 'Province' }),
  Netherlands: fmt({ stateLabel: null }),
  Belgium: fmt({ stateLabel: null }),
  Austria: fmt({ stateLabel: null }),
  Switzerland: fmt({ stateLabel: null }),
  Denmark: fmt({ stateLabel: null }),
  Norway: fmt({ stateLabel: null }),
  Sweden: fmt({ stateLabel: null }),
  Finland: fmt({ stateLabel: null }),
  Portugal: fmt({ stateLabel: null }),
  Poland: fmt({ stateLabel: null }),
  Israel: fmt({ stateLabel: null }),
};

export const addressFormat = (country: string | undefined): AddressFormat =>
  (country && FORMATS[country]) || DEFAULT_FORMAT;

/** Every country the address field's country dropdown offers, alphabetical. */
export const COUNTRIES: string[] = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia',
  'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Central African Republic', 'Chad',
  'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
  'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
  'Guyana', 'Haiti', 'Honduras', 'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq',
  'Ireland', 'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati',
  'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein',
  'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
  'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco',
  'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger',
  'Nigeria', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Panama', 'Papua New Guinea', 'Paraguay',
  'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis',
  'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan',
  'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste',
  'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City',
  'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];

// ---- sub-field metadata (Logic pickers, prefill paths) -----------------------

export interface CompositeSub {
  sub: string;
  label: string;
  /** recomputed from the other parts — readable by rules, never a prefill target */
  derived?: boolean;
}

/** The sub-fields a composite field exposes to rules, as `fieldKey.sub` paths. */
export function compositeSubs(kind: 'name' | 'address', shipping = false): CompositeSub[] {
  if (kind === 'name') {
    return [
      { sub: 'firstName', label: 'First Name' },
      { sub: 'lastName', label: 'Last Name' },
      { sub: 'fullName', label: 'Full Name (combined)', derived: true },
    ];
  }
  return [
    ...(shipping ? [{ sub: 'company', label: 'Company / Name' }, { sub: 'attention', label: 'Attention' }] : []),
    { sub: 'addressLine1', label: 'Address Line 1' },
    { sub: 'addressLine2', label: 'Address Line 2' },
    { sub: 'city', label: 'City' },
    { sub: 'state', label: 'State / Province' },
    { sub: 'postalCode', label: 'Postal / ZIP Code' },
    { sub: 'country', label: 'Country' },
    ...(shipping ? [{ sub: 'phone', label: 'Phone Number' }] : []),
    { sub: 'fullAddress', label: 'Full Address (combined)', derived: true },
  ];
}

// ---- shared display helpers -------------------------------------------------

// Legacy short keys (line1/first/…) still appear in early stored answers.
const COMPOSITE_KEYS = [
  'firstName', 'lastName', 'first', 'last', 'company', 'attention',
  'addressLine1', 'line1', 'addressLine2', 'line2', 'city', 'state', 'postalCode', 'postal', 'country', 'phone',
];
const DERIVED_KEYS = new Set(['fullName', 'fullAddress']);

const isScalarRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)
  && Object.values(v).every((x) => x === null || x === undefined || typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean');

/**
 * One line of text out of a composite (name/address) answer, or null when the
 * value is not a flat object of scalars. The stored combined value is used
 * when present; otherwise the parts join (a name with spaces, an address with
 * commas).
 */
export function formatCompositeAnswer(v: unknown): string | null {
  if (!isScalarRecord(v)) return null;
  const full = String(v.fullName ?? v.fullAddress ?? '').trim();
  if (full) return full;
  const keys = Object.keys(v).filter((k) => !DERIVED_KEYS.has(k));
  const ordered = [
    ...COMPOSITE_KEYS.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !COMPOSITE_KEYS.includes(k)),
  ];
  const parts = ordered.map((k) => String(v[k] ?? '').trim()).filter(Boolean);
  if (!parts.length) return '—';
  const nameOnly = keys.every((k) => ['first', 'last', 'firstName', 'lastName'].includes(k));
  return parts.join(nameOnly ? ' ' : ', ');
}

/** Is a composite answer effectively blank (absent, or all sub-answers empty)? */
/**
 * A composite answer with every part trimmed — what gets submitted.
 *
 * Sub-answers are stored as typed while the form is open, so the trim happens
 * once, here, on the way out. The derived combined value is already trimmed.
 */
export function trimComposite(v: unknown): unknown {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return v;
  const out: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
    out[k] = typeof x === 'string' ? (x.trim() || null) : x;
  }
  return out;
}

export function compositeBlank(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return true;
  if (typeof v !== 'object' || Array.isArray(v)) return false;
  // The country sub-answer alone doesn't make an address "filled in" — it is
  // often just the pre-selected default. Derived keys recompute from the rest.
  return Object.entries(v).every(([k, x]) =>
    k === 'country' || DERIVED_KEYS.has(k) || x === undefined || x === null || String(x).trim() === '');
}
